import type { ModuleLoggerAPI, Track } from '@ad-lucy/utils';
import { signal } from '@preact/signals-core';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { LoggerService } from '../LoggerService';
import type { HoverTrackerCallbacks, UseOnHoverTracker } from './types';
import { useCoordinates } from './useCoordinates';
import { useTracker } from './useTracker';

// Global signal to track which hover tracker is currently active, preventing multiple trackers from firing simultaneously
export const currentHoverTrackerId = signal<string | null>(null);
export const DEFAULT_HOVER_DURATION = 1500;

const safelyClearTimeout = (timeoutRef: { current: number | undefined }): void => {
  if (typeof timeoutRef.current === 'number') {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  }
};

const logger = (): ModuleLoggerAPI =>
  LoggerService.getLogger().getModuleLogger('useOnHoverTracker');

export const useOnHoverTracker: UseOnHoverTracker = (options) => {
  const {
    callback,
    column,
    component,
    external,
    meta,
    payout,
    defaultPayout,
    product,
    stage,
    trigger,
    minHoverDuration = DEFAULT_HOVER_DURATION,
    maxHoverDuration,
    once,
  } = options;

  const trackerId = useId();

  const track: Track = useTracker();
  const hasFiredOnce = useRef(false);
  const minHoverTimeout = useRef<number>();
  const maxHoverTimeout = useRef<number>();
  const element = useRef<EventTarget>();
  const elementClicked = useRef(false);
  const hasFiredForThisHover = useRef(false);
  const [shouldFireTracker, setShouldFireTracker] = useState(false);
  const getCoordinates = useCoordinates();

  const onElementClick = useCallback(() => {
    elementClicked.current = true;
  }, []);

  const safelyClearTimeouts = useCallback(() => {
    safelyClearTimeout(minHoverTimeout);
    safelyClearTimeout(maxHoverTimeout);
  }, []);

  const onMouseEnter: HoverTrackerCallbacks['onMouseEnter'] = useCallback(
    (e) => {
      // Hover should not be tracked if the user clicks on an element
      e?.target?.addEventListener('click', onElementClick);
      element.current = e.target;
      elementClicked.current = false;

      // Check if another tracker is active
      if (currentHoverTrackerId.value) {
        return;
      }

      if (once && hasFiredOnce.current) {
        return;
      }

      // Set this tracker as active
      currentHoverTrackerId.value = trackerId;
      hasFiredForThisHover.current = false;

      // Wait for the user to hover for minHoverDuration
      minHoverTimeout.current = window.setTimeout(() => {
        setShouldFireTracker(true);
      }, minHoverDuration);

      // Prevent tracking if the user is hovering for more than the maxHoverDuration
      if (typeof maxHoverDuration === 'number') {
        maxHoverTimeout.current = window.setTimeout(() => {
          setShouldFireTracker(false);
        }, maxHoverDuration);
      }
    },
    [minHoverDuration, maxHoverDuration, once, onElementClick, trackerId],
  );

  const onMouseLeave: HoverTrackerCallbacks['onMouseLeave'] = useCallback(
    (e, onDemandOptions?) => {
      // Clear the active tracker if it's this instance
      if (currentHoverTrackerId.value === trackerId) {
        currentHoverTrackerId.value = null;
      }

      callback?.(e);
      // Prevent tracking if the user didn't hover for the minHoverDuration
      safelyClearTimeouts();
      // To make sure MouseX and MouseY being sent for touch devices as well
      const { mouseX, mouseY } = getCoordinates(e);
      if (shouldFireTracker && !elementClicked.current && !hasFiredForThisHover.current) {
        hasFiredForThisHover.current = true;
        track({
          event: 'Hover',
          mouseX,
          mouseY,
          column,
          component,
          external,
          meta,
          payout,
          defaultPayout,
          product,
          stage,
          trigger,
          ...onDemandOptions,
        }).catch(logger().error);

        hasFiredOnce.current = true;
        setShouldFireTracker(false);
        e?.target?.removeEventListener('click', onElementClick);
      }
    },
    [
      callback,
      safelyClearTimeouts,
      shouldFireTracker,
      onElementClick,
      track,
      column,
      component,
      external,
      meta,
      payout,
      defaultPayout,
      product,
      stage,
      trigger,
      getCoordinates,
      trackerId,
    ],
  );

  const cleanup = useCallback(() => {
    if (currentHoverTrackerId.value === trackerId) {
      currentHoverTrackerId.value = null;
    }
    if (element.current) {
      element.current.removeEventListener('click', onElementClick);
    }
    safelyClearTimeouts();
  }, [onElementClick, safelyClearTimeouts, trackerId]);

  useEffect(() => cleanup, [cleanup]);

  if (typeof maxHoverDuration === 'number' && maxHoverDuration <= minHoverDuration) {
    logger().warn(
      'maxHoverDuration is smaller or equal to minHoverDuration. Did you forget to set a minHoverDuration?',
    );
  }

  return { onMouseEnter, onMouseLeave };
};
